import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IAppOption } from '~/types/option'
import type { IReportReviewMacro, IReviewReportMacroPayload } from '~/types/review'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import CreatableSelectInput from '~/components/Inputs/ReactHookForm/CreatableSelectInput'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import CustomAppOption from '~/components/SelectOptions/CustomAppOption'
import { FORM_ERRORS } from '~/const'
import useApps from '~/hooks/useApps'
import {
  useCreateReviewReportMacroMutation,
  keys as reportMacroKeys,
  useUpdateReviewReportMacroMutation,
} from '~/queries/reviews-report-macros'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { RequiredYupOption, YupAppOption } from '~/utils/yup'

import './../styles.scss'

const MODAL_ID = 'manage-report-macro-modal'

interface FormValues {
  applications: IAppOption[]
  content: string
  keywords: IOption[]
  reason: IOption | null
  subject: IOption | null
}

const defaultCreateValues: FormValues = {
  applications: [],
  content: '',
  keywords: [],
  reason: null,
  subject: null,
}

const validationSchema = Yup.object().shape({
  applications: Yup.array().of(YupAppOption).required().min(1, FORM_ERRORS.AT_LEAST_ONE),
  content: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  keywords: Yup.array().of(RequiredYupOption).required(),
  reason: RequiredYupOption.nullable().notOneOf([null], FORM_ERRORS.REQUIRED),
  subject: RequiredYupOption.nullable().notOneOf([null], FORM_ERRORS.REQUIRED),
})

interface FormProps {
  macro?: IReportReviewMacro
  macros: IReportReviewMacro[]
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
}

const ManageReportMacroModalForm = ({ macro, macros, onClose, onFailure, onSuccess }: FormProps): ReactElement => {
  /** Local state */

  const { appsOptions, isAppsLoading } = useApps()

  const defaultValues: FormValues = macro
    ? {
        applications:
          macro.applications
            ?.map(
              ({ app_key, device_type }) =>
                appsOptions.find((el) => el.appKey === app_key && el.deviceType === device_type) as IAppOption
            )
            .filter((el) => el !== undefined) || [],
        content: macro.content,
        keywords: macro.keywords.map((el) => ({ label: el, value: el })),
        reason: {
          label: macro.reason,
          value: macro.reason,
        },
        subject: {
          label: macro.subject,
          value: macro.subject,
        },
      }
    : defaultCreateValues

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    resetField,
    watch,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const keywordsOptions: IOption[] = [...new Set(macros.map(({ keywords }) => keywords).flat())].map((keyword) => ({
    label: keyword,
    value: keyword,
  }))

  const reasonOptions = [...new Set(macros.map(({ reason }) => reason))].map((value) => ({
    label: value,
    value: value,
  }))

  const reasonWatch = watch('reason')

  const subjectOptions = [
    ...new Set(macros.filter(({ reason }) => reason === reasonWatch?.value).map(({ subject }) => subject)),
  ].map((value) => ({
    label: value,
    value: value,
  }))

  /** Queries & Mutations */

  const { mutateAsync: createReviewReportMacro } = useCreateReviewReportMacroMutation()

  const { mutateAsync: updateReviewReportMacro } = useUpdateReviewReportMacroMutation()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    try {
      const selectedApps = values.applications?.map(({ value }) => JSON.parse(value)) || []
      const isAllApps = values.applications?.length === appsOptions?.length
      const payload: IReviewReportMacroPayload = {
        applications: isAllApps ? [] : selectedApps,
        content: values.content,
        is_all_apps: isAllApps,
        keywords: values.keywords.map(({ value }) => value),
        reason: values.reason?.value || '',
        subject: values.subject?.value || '',
      }
      if (macro) {
        await updateReviewReportMacro({ macroId: macro.id, payload })
      } else {
        await createReviewReportMacro(payload)
      }
      await queryClient.invalidateQueries({ queryKey: reportMacroKeys.list() })
      onSuccess()
    } catch (e) {
      captureException(e)
      onFailure()
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <Controller
            control={control}
            name="applications"
            render={({ field }) => (
              <MultiSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                components={{ Option: CustomAppOption }}
                error={errors.applications?.message}
                isLoading={isAppsLoading}
                label="Applications"
                options={appsOptions}
                placeholder="Select one or more applications"
                required
              />
            )}
          />
          <Controller
            control={control}
            name="reason"
            render={({ field }) => (
              <SelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors.reason?.message}
                label="Concern"
                onChange={(e) => {
                  field.onChange(e)
                  resetField('subject')
                }}
                options={reasonOptions}
                placeholder="Select a concern"
                required
              />
            )}
          />
          <Controller
            control={control}
            name="subject"
            render={({ field }) => (
              <CreatableSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                disabled={!reasonWatch}
                error={errors.subject?.message}
                label="Subject"
                options={subjectOptions}
                placeholder="Select or type a subject and hit enter"
                required
              />
            )}
          />
          <TextArea
            {...register('content')}
            error={errors.content?.message}
            label="Macro"
            placeholder="Enter content"
            required
          />
          <Controller
            control={control}
            name="keywords"
            render={({ field }) => (
              <CreatableSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors.keywords?.message}
                isMulti
                label="Automation Keywords"
                options={keywordsOptions}
                placeholder="Select or enter keywords"
              />
            )}
          />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ManageReportMacroModal = ({ isOpen, macro, macros, onClose, onFailure, onSuccess }: ModalProps): ReactElement => {
  return (
    <Modal className="reviews__manage-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{macro ? 'Edit' : 'Create'} macro</ModalHeader>
      <ManageReportMacroModalForm
        macro={macro}
        macros={macros}
        onClose={onClose}
        onFailure={onFailure}
        onSuccess={onSuccess}
      />
    </Modal>
  )
}

export default ManageReportMacroModal
